import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getSeekerProfile,
  createOrUpdateSeekerProfile,
  uploadResume,
  checkProfileCompletion
} from '../services/seekerProfileService';
import { getUserData } from '../services/userService';
import { getUserActiveSubscription } from '../services/subscriptionService';
import { notifyAndroidProfileUpdate } from '../services/androidBridge';
import LoadingSpinner from '../components/LoadingSpinner';
import { locationsByState } from '../constants/locations';
import { commonJobRoles } from '../constants/jobRoles';
import EnhancedChipInput from '../components/EnhancedChipInput';
import Footer from '../components/Footer';

// Bilingual Label Component
const BilingualLabel = ({ english, marathi, required = false, className = "" }) => (
  <label className={`block text-sm font-medium text-gray-700 ${className}`}>
    {english} <span className="text-gray-500">/ {marathi}</span>
    {required && <span className="text-red-500 ml-1">*</span>}
  </label>
);

const SeekerProfile = ({ user, userData, viewMode = false }) => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewedUserData, setViewedUserData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [completionStats, setCompletionStats] = useState({ completionPercentage: 0, isComplete: false });
  const [activeSection, setActiveSection] = useState('personal');
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [resumeUploaded, setResumeUploaded] = useState(false);
  const [showPrereqModal, setShowPrereqModal] = useState(false);
  const [prereqSubscribed, setPrereqSubscribed] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    personalInfo: {
      firstName: userData?.firstName || '',
      lastName: userData?.lastName || '',
      email: userData?.email || '',
      phone: userData?.phone || '',
      city: '',
      state: '',
      district: '',
      location: '', // Deprecated but kept for compatibility if needed
      dateOfBirth: '',
      gender: '',
      linkedIn: '',
      portfolio: ''
    },
    professionalSummary: '',
    experience: [],
    education: [],
    skills: [],
    certifications: [],
    languages: [],
    resumeURL: '',
    resumeFileName: '',
    preferences: {
      jobTypes: [],
      workModes: [],
      expectedSalary: { min: '', max: '', currency: 'USD' },
      noticePeriod: '',
      willingToRelocate: false,
      targetRoles: []
    }
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Skip user type check if in view mode
    if (!viewMode && userData?.userType !== 'jobseeker') {
      navigate('/dashboard');
      return;
    }

    fetchProfile();
  }, [user, userData, navigate, viewMode, userId]);

  useEffect(() => {
    // Calculate completion percentage whenever form data changes
    const stats = checkProfileCompletion(formData);
    setCompletionStats(stats);
  }, [formData]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      // Determine which user ID to fetch
      const targetUserId = viewMode && userId ? userId : user?.uid;

      if (!targetUserId) {
        setLoading(false);
        return;
      }

      // If viewing another user's profile, fetch their user data
      if (viewMode && userId) {
        const userDataResult = await getUserData(userId);
        if (userDataResult) {
          setViewedUserData(userDataResult);
        }
      }

      const result = await getSeekerProfile(targetUserId);
      if (result.success && result.profile) {
        setProfile(result.profile);
        if (!viewMode) {
          if (result.profile.resumeURL) {
            setResumeUploaded(true);
          }
          setFormData(prev => ({
            ...prev,
            ...result.profile,
            personalInfo: {
              ...prev.personalInfo,
              ...(result.profile.personalInfo || {})
            },
            preferences: {
              ...prev.preferences,
              ...(result.profile.preferences || {})
            }
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => {
      // Special handling for personalInfo location string update
      if (section === 'personalInfo' && (field === 'city' || field === 'state')) {
        const currentInfo = prev.personalInfo;
        const newCity = field === 'city' ? value : currentInfo.city;
        const newState = field === 'state' ? value : currentInfo.state;

        // Construct location string: "City, State, India"
        const parts = [];
        if (newCity) parts.push(newCity);
        if (newState) parts.push(newState);
        if (newState || newCity) parts.push("India");
        const newLocation = parts.join(', ');

        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value,
            location: newLocation,
            // Reset district if state changes (logic handled in UI component too, but safe here)
            ...(field === 'state' ? { district: '' } : {})
          }
        };
      }

      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      };
    });

    // Clear error for this field
    if (errors[`${section}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${section}.${field}`]: ''
      }));
    }
  };

  const handleArrayAdd = (section, newItem) => {
    setFormData(prev => ({
      ...prev,
      [section]: [...prev[section], newItem]
    }));
  };

  const handleArrayUpdate = (section, index, updatedItem) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => i === index ? updatedItem : item)
    }));
  };

  const handleArrayRemove = (section, index) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  const handleResumeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingResume(true);
    try {
      const result = await uploadResume(user.uid, file);
      if (result.success) {
        setFormData(prev => ({
          ...prev,
          resumeURL: result.downloadURL,
          resumeFileName: result.fileName
        }));
        setSuccessMessage('Resume uploaded successfully!');
        setResumeUploaded(true);

        // Check if user already has an active subscription
        const subscriptionResult = await getUserActiveSubscription(user.uid);
        const hasSubscription = subscriptionResult.success && subscriptionResult.subscription;

        if (hasSubscription) {
          // Already paid — auto-advance to Job Preferences section
          setTimeout(() => {
            setActiveSection('preferences');
            setSuccessMessage('Resume uploaded! Fill in your job preferences to complete your profile.');
          }, 600);
        } else {
          // Not yet subscribed — show payment modal after short delay
          setTimeout(() => setShowPaymentModal(true), 800);
        }
      } else {
        setErrors(prev => ({ ...prev, resume: result.error }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, resume: 'Failed to upload resume' }));
    } finally {
      setUploadingResume(false);
    }
  };

  const getValidationErrors = (data, section = null) => {
    const errors = {};

    // Personal Info
    if (!section || section === 'personal') {
      if (!data.personalInfo.firstName.trim()) errors['personalInfo.firstName'] = 'First name is required';
      if (!data.personalInfo.lastName.trim()) errors['personalInfo.lastName'] = 'Last name is required';
      if (!data.personalInfo.email.trim()) errors['personalInfo.email'] = 'Email is required';
      if (!data.personalInfo.phone.trim()) errors['personalInfo.phone'] = 'Phone is required';
      if (!data.personalInfo.phone.trim()) errors['personalInfo.phone'] = 'Phone is required';
      if (!data.personalInfo.state) errors['personalInfo.state'] = 'State is required';
      if (!data.personalInfo.district) errors['personalInfo.district'] = 'District is required';
      if (!data.personalInfo.city?.trim()) errors['personalInfo.city'] = 'City is required';
    }

    // Professional Summary
    if (!section || section === 'summary') {
      if (!data.professionalSummary || !data.professionalSummary.trim()) errors['professionalSummary'] = 'Professional Summary is required';
    }

    // Experience
    if ((!section || section === 'experience') && data.preferences?.careerLevel !== 'fresher') {
      data.experience.forEach((exp, index) => {
        if (!exp.jobTitle?.trim()) errors[`experience.${index}.jobTitle`] = 'Job Title is required';
        if (!exp.company?.trim()) errors[`experience.${index}.company`] = 'Company Name is required';
        if (!exp.startDate?.trim()) errors[`experience.${index}.startDate`] = 'Start Date is required';
        if (!exp.isCurrentJob && !exp.endDate?.trim()) errors[`experience.${index}.endDate`] = 'End Date is required';
        if (!exp.description?.trim()) errors[`experience.${index}.description`] = 'Job Description is required';
      });
    }

    // Education
    if (!section || section === 'education') {
      data.education.forEach((edu, index) => {
        if (!edu.degree?.trim()) errors[`education.${index}.degree`] = 'Degree is required';
        if (!edu.institution?.trim()) errors[`education.${index}.institution`] = 'Institution is required';
        if (!edu.graduationYear?.toString().trim()) errors[`education.${index}.graduationYear`] = 'Graduation Year is required';
      });
    }

    // Skills
    if (!section || section === 'skills') {
      if (!data.skills || data.skills.length === 0) errors['skills'] = 'At least one skill is required';
    }

    // Resume
    if (!section || section === 'resume') {
      if (!data.resumeURL) errors['resume'] = 'Resume is required';
    }

    // Preferences
    if (!section || section === 'preferences') {
      if (!data.preferences.jobTypes?.length) errors['preferences.jobTypes'] = 'Select at least one job type';
      if (!data.preferences.workModes?.length) errors['preferences.workModes'] = 'Select at least one work mode';
      if (!data.preferences.expectedSalary?.min) errors['preferences.expectedSalary.min'] = 'Min salary is required';
      if (!data.preferences.expectedSalary?.max) errors['preferences.expectedSalary.max'] = 'Max salary is required';
      if (!data.preferences.noticePeriod) errors['preferences.noticePeriod'] = 'Notice period is required';
      if (!data.preferences.targetRoles?.length) errors['preferences.targetRoles'] = 'Add at least one job role';
    }

    return errors;
  };

  const validateForm = () => {
    const newErrors = getValidationErrors(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSectionChange = async (nextSection) => {
    // Save draft of current data to Firestore when switching sections
    try {
      if (user?.uid) {
        await createOrUpdateSeekerProfile(user.uid, formData);
      }
    } catch (e) {
      console.warn('Auto-save on section change failed:', e);
    }

    const sectionErrors = getValidationErrors(formData, activeSection);
    if (Object.keys(sectionErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...sectionErrors }));
    }

    // Always allow navigation so they can fill out other sections first
    setActiveSection(nextSection);
    if (window.innerWidth < 1024) {
      setTimeout(() => {
        document.getElementById('profile-main-content')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleSave = async () => {
    // Validate all sections
    const allErrors = {};

    // Check Personal Info
    const personalErrors = getValidationErrors(formData, 'personal');
    if (Object.keys(personalErrors).length > 0) {
      Object.assign(allErrors, personalErrors);
    }

    // Check Professional Summary
    const summaryErrors = getValidationErrors(formData, 'summary');
    Object.assign(allErrors, summaryErrors);

    // Check Experience (only if not fresher)
    const isFresher = formData.preferences?.careerLevel === 'fresher';
    let experienceErrors = {};
    if (!isFresher) {
      experienceErrors = getValidationErrors(formData, 'experience');
      Object.assign(allErrors, experienceErrors);
    }

    // Check Education
    const educationErrors = getValidationErrors(formData, 'education');
    Object.assign(allErrors, educationErrors);

    // Check Skills
    const skillsErrors = getValidationErrors(formData, 'skills');
    Object.assign(allErrors, skillsErrors);

    // Check Resume
    const resumeErrors = getValidationErrors(formData, 'resume');
    Object.assign(allErrors, resumeErrors);

    // Check Preferences
    const preferenceErrors = getValidationErrors(formData, 'preferences');
    Object.assign(allErrors, preferenceErrors);

    const hasErrors = Object.keys(allErrors).length > 0;

    setSaving(true);
    try {
      const result = await createOrUpdateSeekerProfile(user.uid, formData);
      if (result.success) {
        setProfile(result.profile);
        
        if (hasErrors) {
          setSuccessMessage('Profile draft saved successfully! Complete all sections to reach 100% completion.');
          setErrors(allErrors);

          // Determine which section has the first error to switch to it
          if (Object.keys(personalErrors).length > 0) setActiveSection('personal');
          else if (Object.keys(summaryErrors).length > 0) setActiveSection('summary');
          else if (!isFresher && Object.keys(experienceErrors).length > 0) setActiveSection('experience');
          else if (Object.keys(educationErrors).length > 0) setActiveSection('education');
          else if (Object.keys(skillsErrors).length > 0) setActiveSection('skills');
          else if (Object.keys(resumeErrors).length > 0) setActiveSection('resume');
          else if (Object.keys(preferenceErrors).length > 0) setActiveSection('preferences');
        } else {
          setSuccessMessage('Profile saved successfully! Checking subscription...');
          setErrors({});

          // Notify Android WebView host with updated preferences — kicks off notification matching
          notifyAndroidProfileUpdate(user.uid, formData.preferences, formData.personalInfo);

          // Check if user has an active subscription
          const subscriptionResult = await getUserActiveSubscription(user.uid);
          const hasActiveSubscription = subscriptionResult.success && subscriptionResult.subscription;

          if (hasActiveSubscription) {
            // Already subscribed — go to dashboard
            setTimeout(() => navigate('/dashboard'), 1500);
          } else {
            // No subscription — redirect to packages page
            setTimeout(() => navigate('/packages'), 1500);
          }
        }
      } else {
        setErrors(prev => ({ ...prev, general: result.error }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, general: 'Failed to save profile' }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const sections = [
    { id: 'personal', name: 'Personal Info', icon: '👤' },
    { id: 'summary', name: 'Professional Summary', icon: '📝' },
    { id: 'experience', name: 'Experience', icon: '💼' },
    { id: 'education', name: 'Education', icon: '🎓' },
    { id: 'skills', name: 'Skills', icon: '⚡' },
    { id: 'resume', name: 'Resume', icon: '📄' },
    { id: 'preferences', name: 'Job Preferences', icon: '⚙️' }
  ];

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        {/* Header */}
        <div className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white mb-4">
                {profile ? 'Update Your Profile' : 'Create Your Profile'}
              </h1>
              <p className="text-xl text-blue-100 mb-6">
                Complete your profile to unlock job opportunities
              </p>

              {/* Completion Progress */}
              <div className="max-w-md mx-auto">
                <div className="flex items-center justify-between text-white mb-2">
                  <span className="text-sm font-medium">Profile Completion</span>
                  <span className="text-sm font-bold">{Math.max(completionStats.completionPercentage, resumeUploaded ? 90 : 0)}%</span>
                </div>
                <div className="w-full bg-white bg-opacity-20 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-green-400 to-emerald-500 h-3 rounded-full transition-all duration-700"
                    style={{ width: `${Math.max(completionStats.completionPercentage, resumeUploaded ? 90 : 0)}%` }}
                  ></div>
                </div>
                {resumeUploaded ? (
                  <p className="text-green-300 text-sm mt-2 font-semibold animate-pulse">
                    🎉 Great! Resume uploaded — Subscribe to complete your profile!
                  </p>
                ) : completionStats.completionPercentage < 85 ? (
                  <p className="text-yellow-200 text-sm mt-2">
                    Complete at least 85% to apply for jobs
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Profile Sections</h3>
                <nav className="space-y-2">
                  {sections.map((section) => {
                    return (
                      <button
                        key={section.id}
                        onClick={() => handleSectionChange(section.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${activeSection === section.id
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-indigo-600'
                          }`}
                      >
                        <span className="text-lg">{section.icon}</span>
                        <span className="font-medium flex-1">{section.name}</span>
                      </button>
                    );
                  })}
                </nav>

                {/* Save Button */}
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </div>

            {/* Main Content */}
            <div id="profile-main-content" className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                {/* Success Message */}
                {successMessage && (
                  <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-green-800 font-medium">{successMessage}</p>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {errors.general && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="text-red-800 font-medium">{errors.general}</p>
                    </div>
                  </div>
                )}

                {/* Dynamic Section Content */}
                {activeSection === 'personal' && (
                  <PersonalInfoSection
                    data={formData.personalInfo}
                    onChange={(field, value) => handleInputChange('personalInfo', field, value)}
                    errors={errors}
                  />
                )}

                {activeSection === 'summary' && (
                  <ProfessionalSummarySection
                    data={formData.professionalSummary}
                    onChange={(value) => setFormData(prev => ({ ...prev, professionalSummary: value }))}
                    errors={errors}
                  />
                )}

                {activeSection === 'experience' && (
                  <ExperienceSection
                    data={formData.experience}
                    onAdd={(item) => handleArrayAdd('experience', item)}
                    onUpdate={(index, item) => handleArrayUpdate('experience', index, item)}
                    onRemove={(index) => handleArrayRemove('experience', index)}
                    errors={errors}
                  />
                )}

                {activeSection === 'education' && (
                  <EducationSection
                    data={formData.education}
                    onAdd={(item) => handleArrayAdd('education', item)}
                    onUpdate={(index, item) => handleArrayUpdate('education', index, item)}
                    onRemove={(index) => handleArrayRemove('education', index)}
                    errors={errors}
                  />
                )}

                {activeSection === 'skills' && (
                  <SkillsSection
                    data={formData.skills}
                    onChange={(skills) => setFormData(prev => ({ ...prev, skills }))}
                    errors={errors}
                  />
                )}

                {activeSection === 'resume' && (
                  <ResumeSection
                    resumeURL={formData.resumeURL}
                    resumeFileName={formData.resumeFileName}
                    onUpload={handleResumeUpload}
                    uploading={uploadingResume}
                    error={errors.resume}
                  />
                )}

                {activeSection === 'preferences' && (
                  <JobPreferencesSection
                    data={formData.preferences}
                    onChange={(field, value) => handleInputChange('preferences', field, value)}
                    errors={errors}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Prerequisite Modal - shown when clicking Job Preferences without completing steps */}
      {showPrereqModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(10,5,30,0.80)', backdropFilter: 'blur(8px)' }}
        >
          <div
            className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
            style={{ animation: 'modalSlideUp 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 via-rose-500 to-pink-600 p-6 text-white text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(circle at 30% 70%, #fff 0%, transparent 60%)' }}></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-3 border-2 border-white border-opacity-30">
                  <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <h2 className="text-xl font-extrabold mb-1">Complete Formalities First!</h2>
                <p className="text-orange-100 text-sm">A few steps are pending before you can set Job Preferences</p>
              </div>
              {/* Close X */}
              <button
                onClick={() => setShowPrereqModal(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Checklist Body */}
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-5 text-center">Complete all the steps below to unlock Job Preferences</p>

              <div className="space-y-3 mb-6">
                {/* Step 1: Resume */}
                <div className={`flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${formData.resumeURL
                    ? 'border-green-200 bg-green-50'
                    : 'border-orange-200 bg-orange-50'
                  }`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${formData.resumeURL ? 'bg-green-500' : 'bg-orange-400'
                    }`}>
                    {formData.resumeURL ? (
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${formData.resumeURL ? 'text-green-800' : 'text-orange-800'}`}>
                      {formData.resumeURL ? '✅ Resume Uploaded' : '📄 Upload Your Resume'}
                    </p>
                    <p className={`text-xs mt-0.5 ${formData.resumeURL ? 'text-green-600' : 'text-orange-600'}`}>
                      {formData.resumeURL ? 'Resume is ready' : 'Go to Resume section and upload your CV/resume'}
                    </p>
                  </div>
                  {!formData.resumeURL && (
                    <button
                      onClick={() => { setShowPrereqModal(false); setActiveSection('resume'); }}
                      className="text-xs font-bold px-3 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex-shrink-0"
                    >
                      Go →
                    </button>
                  )}
                </div>

                {/* Step 2: Subscription */}
                <div className={`flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${prereqSubscribed
                    ? 'border-green-200 bg-green-50'
                    : 'border-purple-200 bg-purple-50'
                  }`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${prereqSubscribed ? 'bg-green-500' : 'bg-purple-500'
                    }`}>
                    {prereqSubscribed ? (
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${prereqSubscribed ? 'text-green-800' : 'text-purple-800'}`}>
                      {prereqSubscribed ? '✅ Subscription Active' : '💳 Subscribe to a Plan'}
                    </p>
                    <p className={`text-xs mt-0.5 ${prereqSubscribed ? 'text-green-600' : 'text-purple-600'}`}>
                      {prereqSubscribed ? 'Your plan is active' : 'Choose a plan to unlock full profile & job applications'}
                    </p>
                  </div>
                  {!prereqSubscribed && (
                    <button
                      onClick={async () => {
                        try { await createOrUpdateSeekerProfile(user.uid, formData); } catch (e) { }
                        setShowPrereqModal(false);
                        navigate('/packages');
                      }}
                      className="text-xs font-bold px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex-shrink-0"
                    >
                      Pay →
                    </button>
                  )}
                </div>
              </div>

              {/* If all done, show unlock button */}
              {formData.resumeURL && prereqSubscribed ? (
                <button
                  onClick={() => { setShowPrereqModal(false); setActiveSection('preferences'); }}
                  className="w-full py-3 px-6 rounded-2xl font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg transition-all"
                >
                  🎉 Open Job Preferences
                </button>
              ) : (
                <button
                  onClick={() => setShowPrereqModal(false)}
                  className="w-full py-3 px-6 rounded-2xl font-semibold text-gray-600 text-sm border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
                >
                  Close & Continue Editing
                </button>
              )}
            </div>
          </div>

          <style>{`
            @keyframes modalSlideUp {
              from { opacity: 0; transform: translateY(40px) scale(0.95); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}</style>
        </div>
      )}

      {/* Payment Modal - shown after resume upload when user has no subscription */}
      {showPaymentModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(15,10,40,0.82)', backdropFilter: 'blur(8px)' }}
        >
          <div
            className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
            style={{ animation: 'modalSlideUp 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}
          >
            {/* Gradient Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-6 text-white text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(circle at 70% 30%, #fff 0%, transparent 60%)' }}></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-3 border-2 border-white border-opacity-30">
                  <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-extrabold mb-1">Resume Uploaded! 🎉</h2>
                <p className="text-indigo-100 text-sm font-medium">Your profile is 90% complete</p>
                {/* Mini progress bar */}
                <div className="mt-3 w-48 mx-auto bg-white bg-opacity-20 rounded-full h-2.5">
                  <div
                    className="bg-gradient-to-r from-green-300 to-emerald-400 h-2.5 rounded-full transition-all duration-1000"
                    style={{ width: '90%' }}
                  ></div>
                </div>
                <p className="text-white text-xs mt-1 font-semibold">90% Complete</p>
              </div>
              {/* No X close button — user must choose a plan to proceed */}
            </div>

            {/* Body */}
            <div className="p-6">
              <div className="text-center mb-5">
                <h3 className="text-lg font-bold text-gray-900">Subscribe to Unlock Full Access</h3>
                <p className="text-gray-500 text-sm mt-1">
                  A subscription is required to complete your profile and start applying for jobs.
                </p>
              </div>

              {/* Feature highlights */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {[
                  { icon: '📝', label: 'Apply for Jobs' },
                  { icon: '🔔', label: 'Job Alerts' },
                  { icon: '⭐', label: 'Profile Boost' },
                ].map((item) => (
                  <div key={item.label} className="text-center p-3 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                    <div className="text-2xl mb-1">{item.icon}</div>
                    <p className="text-xs font-semibold text-indigo-700">{item.label}</p>
                  </div>
                ))}
              </div>

              {/* Single CTA Button - no Continue Editing option */}
              <button
                onClick={async () => {
                  // Auto-save current profile data before navigating to packages
                  try {
                    await createOrUpdateSeekerProfile(user.uid, formData);
                  } catch (e) {
                    console.warn('Auto-save before packages failed:', e);
                  }
                  setShowPaymentModal(false);
                  navigate('/packages');
                }}
                className="w-full py-4 px-6 rounded-2xl font-bold text-white text-lg bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                View &amp; Choose a Plan
              </button>

              <p className="text-center text-xs text-gray-400 mt-4">
                🔒 Secure payment powered by Cashfree
              </p>
            </div>
          </div>

          {/* Modal animation keyframes */}
          <style>{`
            @keyframes modalSlideUp {
              from { opacity: 0; transform: translateY(40px) scale(0.95); }
              to   { opacity: 1; transform: translateY(0)  scale(1); }
            }
          `}</style>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </>
  );
};

// Personal Info Section Component
const PersonalInfoSection = ({ data, onChange, errors }) => (
  <div>
    <h2 className="text-2xl font-bold text-gray-900 mb-6">
      Personal Information <span className="text-gray-500">/ वैयक्तिक माहिती</span>
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <BilingualLabel english="First Name" marathi="पहिले नाव" required className="mb-2" />
        <input
          type="text"
          value={data.firstName}
          onChange={(e) => onChange('firstName', e.target.value)}
          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors['personalInfo.firstName'] ? 'border-red-300' : 'border-gray-300'
            }`}
          placeholder="Enter your first name / पहिले नाव प्रविष्ट करा"
        />
        {errors['personalInfo.firstName'] && (
          <p className="text-red-600 text-sm mt-1">{errors['personalInfo.firstName']}</p>
        )}
      </div>

      <div>
        <BilingualLabel english="Last Name" marathi="आडनाव" required className="mb-2" />
        <input
          type="text"
          value={data.lastName}
          onChange={(e) => onChange('lastName', e.target.value)}
          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors['personalInfo.lastName'] ? 'border-red-300' : 'border-gray-300'
            }`}
          placeholder="Enter your last name / आडनाव प्रविष्ट करा"
        />
        {errors['personalInfo.lastName'] && (
          <p className="text-red-600 text-sm mt-1">{errors['personalInfo.lastName']}</p>
        )}
      </div>

      <div>
        <BilingualLabel english="Email" marathi="ईमेल" required className="mb-2" />
        <input
          type="email"
          value={data.email}
          onChange={(e) => onChange('email', e.target.value)}
          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors['personalInfo.email'] ? 'border-red-300' : 'border-gray-300'
            }`}
          placeholder="Enter your email / ईमेल प्रविष्ट करा"
        />
        {errors['personalInfo.email'] && (
          <p className="text-red-600 text-sm mt-1">{errors['personalInfo.email']}</p>
        )}
      </div>

      <div>
        <BilingualLabel english="Phone" marathi="फोन" required className="mb-2" />
        <input
          type="tel"
          value={data.phone}
          onChange={(e) => onChange('phone', e.target.value)}
          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors['personalInfo.phone'] ? 'border-red-300' : 'border-gray-300'
            }`}
          placeholder="Enter your phone number / फोन नंबर प्रविष्ट करा"
        />
        {errors['personalInfo.phone'] && (
          <p className="text-red-600 text-sm mt-1">{errors['personalInfo.phone']}</p>
        )}
      </div>

      <div>
        <BilingualLabel english="State" marathi="राज्य" required className="mb-2" />
        <select
          value={data.state || ''}
          onChange={(e) => {
            onChange('state', e.target.value);
            onChange('district', ''); // Reset district when state changes
          }}
          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors['personalInfo.state'] ? 'border-red-300' : 'border-gray-300'}`}
        >
          <option value="">Select State / राज्य निवडा</option>
          {Object.keys(locationsByState).map(state => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>
        {errors['personalInfo.state'] && (
          <p className="text-red-600 text-sm mt-1">{errors['personalInfo.state']}</p>
        )}
      </div>

      <div>
        <BilingualLabel english="District" marathi="जिल्हा" required className="mb-2" />
        <select
          value={data.district || ''}
          onChange={(e) => onChange('district', e.target.value)}
          disabled={!data.state}
          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 ${errors['personalInfo.district'] ? 'border-red-300' : 'border-gray-300'}`}
        >
          <option value="">Select District / जिल्हा निवडा</option>
          {data.state && locationsByState[data.state]?.map(district => (
            <option key={district} value={district}>{district}</option>
          ))}
        </select>
        {errors['personalInfo.district'] && (
          <p className="text-red-600 text-sm mt-1">{errors['personalInfo.district']}</p>
        )}
      </div>

      <div>
        <BilingualLabel english="City" marathi="शहर" required className="mb-2" />
        <input
          type="text"
          value={data.city || ''}
          onChange={(e) => onChange('city', e.target.value)}
          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors['personalInfo.city'] ? 'border-red-300' : 'border-gray-300'}`}
          placeholder="Enter city / शहर प्रविष्ट करा"
        />
        {errors['personalInfo.city'] && (
          <p className="text-red-600 text-sm mt-1">{errors['personalInfo.city']}</p>
        )}
      </div>

      <div>
        <BilingualLabel english="LinkedIn Profile" marathi="लिंक्डइन प्रोफाइल" className="mb-2" />
        <input
          type="url"
          value={data.linkedIn}
          onChange={(e) => onChange('linkedIn', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="https://linkedin.com/in/yourprofile"
        />
      </div>
    </div>
  </div>
);

// Professional Summary Section Component
const ProfessionalSummarySection = ({ data, onChange, errors }) => (
  <div>
    <h2 className="text-2xl font-bold text-gray-900 mb-6">
      Professional Summary <span className="text-gray-500">/ व्यावसायिक सारांश</span>
    </h2>
    <div>
      <BilingualLabel english="Tell us about yourself and your career goals" marathi="स्वतःबद्दल आणि करिअर ध्येयांबद्दल सांगा" required className="mb-2" />
      <textarea
        value={data}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors?.professionalSummary ? 'border-red-300' : 'border-gray-300'}`}
        placeholder="Write a brief summary of your professional background, key skills, and career objectives..."
      />
      {errors?.professionalSummary && (
        <p className="text-red-600 text-sm mt-1">{errors.professionalSummary}</p>
      )}
      <p className="text-gray-500 text-sm mt-2">
        {data.length}/500 characters
      </p>
    </div>
  </div>
);

// Experience Section Component
const ExperienceSection = ({ data, onAdd, onUpdate, onRemove, errors }) => (
  <div>
    <h2 className="text-2xl font-bold text-gray-900 mb-6">
      Work Experience <span className="text-gray-500">/ कामाचा अनुभव</span>
    </h2>
    <p className="text-gray-600 mb-6">
      Add your work experience starting with the most recent position. <span className="text-gray-500">/ सर्वात अलीकडील पदापासून सुरुवात करून तुमचा कामाचा अनुभव जोडा.</span>
    </p>

    {data.map((exp, index) => (
      <div key={index} className="border border-gray-200 rounded-xl p-6 mb-6 bg-gray-50">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Experience #{index + 1}</h3>
          <button
            onClick={() => onRemove(index)}
            className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Remove
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <BilingualLabel english="Job Title" marathi="नोकरीचे शीर्षक" required className="mb-2" />
            <input
              type="text"
              value={exp.jobTitle || ''}
              onChange={(e) => onUpdate(index, { ...exp, jobTitle: e.target.value })}
              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors[`experience.${index}.jobTitle`] ? 'border-red-300' : 'border-gray-300'
                }`}
              placeholder="e.g. Software Engineer"
            />
            {errors[`experience.${index}.jobTitle`] && (
              <p className="text-red-600 text-sm mt-1">{errors[`experience.${index}.jobTitle`]}</p>
            )}
          </div>
          <div>
            <BilingualLabel english="Company Name" marathi="कंपनीचे नाव" required className="mb-2" />
            <input
              type="text"
              value={exp.company || ''}
              onChange={(e) => onUpdate(index, { ...exp, company: e.target.value })}
              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors[`experience.${index}.company`] ? 'border-red-300' : 'border-gray-300'
                }`}
              placeholder="e.g. Google Inc."
            />
            {errors[`experience.${index}.company`] && (
              <p className="text-red-600 text-sm mt-1">{errors[`experience.${index}.company`]}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
            <input
              type="month"
              value={exp.startDate || ''}
              onChange={(e) => onUpdate(index, { ...exp, startDate: e.target.value })}
              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors[`experience.${index}.startDate`] ? 'border-red-300' : 'border-gray-300'
                }`}
            />
            {errors[`experience.${index}.startDate`] && (
              <p className="text-red-600 text-sm mt-1">{errors[`experience.${index}.startDate`]}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date {!exp.isCurrentJob && <span className="text-red-500">*</span>}
            </label>
            <input
              type="month"
              value={exp.endDate || ''}
              onChange={(e) => onUpdate(index, { ...exp, endDate: e.target.value })}
              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors[`experience.${index}.endDate`] ? 'border-red-300' : 'border-gray-300'
                }`}
              disabled={exp.isCurrentJob}
            />
            {errors[`experience.${index}.endDate`] && (
              <p className="text-red-600 text-sm mt-1">{errors[`experience.${index}.endDate`]}</p>
            )}
            <div className="flex items-center mt-2">
              <input
                type="checkbox"
                id={`current-job-${index}`}
                checked={exp.isCurrentJob || false}
                onChange={(e) => onUpdate(index, {
                  ...exp,
                  isCurrentJob: e.target.checked,
                  endDate: e.target.checked ? '' : exp.endDate
                })}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <label htmlFor={`current-job-${index}`} className="ml-2 text-sm text-gray-600">
                I currently work here
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <input
              type="text"
              value={exp.location || ''}
              onChange={(e) => onUpdate(index, { ...exp, location: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g. San Francisco, CA"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type</label>
            <select
              value={exp.employmentType || ''}
              onChange={(e) => onUpdate(index, { ...exp, employmentType: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select type</option>
              <option value="full-time">Full-time</option>
              <option value="part-time">Part-time</option>
              <option value="contract">Contract</option>
              <option value="freelance">Freelance</option>
              <option value="internship">Internship</option>
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Job Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={exp.description || ''}
            onChange={(e) => onUpdate(index, { ...exp, description: e.target.value })}
            rows={4}
            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors[`experience.${index}.description`] ? 'border-red-300' : 'border-gray-300'
              }`}
            placeholder="Describe your responsibilities, achievements, and key projects..."
          />
          {errors[`experience.${index}.description`] && (
            <p className="text-red-600 text-sm mt-1">{errors[`experience.${index}.description`]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Key Achievements</label>
          <textarea
            value={exp.achievements || ''}
            onChange={(e) => onUpdate(index, { ...exp, achievements: e.target.value })}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="List your key achievements and accomplishments in this role..."
          />
        </div>
      </div>
    ))}

    <button
      onClick={() => onAdd({
        jobTitle: '',
        company: '',
        startDate: '',
        endDate: '',
        location: '',
        employmentType: '',
        description: '',
        achievements: '',
        isCurrentJob: false
      })}
      className="w-full py-4 border-2 border-dashed border-indigo-300 rounded-xl text-indigo-600 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 font-medium flex items-center justify-center gap-2"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
      Add Work Experience
    </button>
  </div>
);

// Education Section Component
const EducationSection = ({ data, onAdd, onUpdate, onRemove, errors }) => (
  <div>
    <h2 className="text-2xl font-bold text-gray-900 mb-6">
      Education <span className="text-gray-500">/ शिक्षण</span>
    </h2>
    <p className="text-gray-600 mb-6">
      Add your educational qualifications starting with the highest degree. <span className="text-gray-500">/ सर्वोच्च पदवीपासून सुरुवात करून तुमची शैक्षणिक पात्रता जोडा.</span>
    </p>

    {data.map((edu, index) => (
      <div key={index} className="border border-gray-200 rounded-xl p-6 mb-6 bg-gray-50">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Education #{index + 1}</h3>
          <button
            onClick={() => onRemove(index)}
            className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Remove
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <BilingualLabel english="Degree/Qualification" marathi="पदवी/पात्रता" required className="mb-2" />
            <input
              type="text"
              value={edu.degree || ''}
              onChange={(e) => onUpdate(index, { ...edu, degree: e.target.value })}
              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors[`education.${index}.degree`] ? 'border-red-300' : 'border-gray-300'
                }`}
              placeholder="e.g. Bachelor of Computer Science"
            />
            {errors[`education.${index}.degree`] && (
              <p className="text-red-600 text-sm mt-1">{errors[`education.${index}.degree`]}</p>
            )}
          </div>
          <div>
            <BilingualLabel english="Institution" marathi="संस्था" required className="mb-2" />
            <input
              type="text"
              value={edu.institution || ''}
              onChange={(e) => onUpdate(index, { ...edu, institution: e.target.value })}
              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors[`education.${index}.institution`] ? 'border-red-300' : 'border-gray-300'
                }`}
              placeholder="e.g. Stanford University"
            />
            {errors[`education.${index}.institution`] && (
              <p className="text-red-600 text-sm mt-1">{errors[`education.${index}.institution`]}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Field of Study</label>
            <input
              type="text"
              value={edu.fieldOfStudy || ''}
              onChange={(e) => onUpdate(index, { ...edu, fieldOfStudy: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g. Computer Science"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Graduation Year *</label>
            <input
              type="text"
              maxLength="4"
              value={edu.graduationYear || ''}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                onUpdate(index, { ...edu, graduationYear: val });
              }}
              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors[`education.${index}.graduationYear`] ? 'border-red-300' : 'border-gray-300'
                }`}
              placeholder="e.g. 2023"
            />
            {errors[`education.${index}.graduationYear`] && (
              <p className="text-red-600 text-sm mt-1">{errors[`education.${index}.graduationYear`]}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Grade/GPA</label>
            <input
              type="text"
              value={edu.grade || ''}
              onChange={(e) => onUpdate(index, { ...edu, grade: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g. 3.8/4.0 or First Class"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <input
              type="text"
              value={edu.location || ''}
              onChange={(e) => onUpdate(index, { ...edu, location: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g. Stanford, CA"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            value={edu.description || ''}
            onChange={(e) => onUpdate(index, { ...edu, description: e.target.value })}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Relevant coursework, projects, achievements, or honors..."
          />
        </div>
      </div>
    ))}

    <button
      onClick={() => onAdd({
        degree: '',
        institution: '',
        fieldOfStudy: '',
        graduationYear: '',
        grade: '',
        location: '',
        description: ''
      })}
      className="w-full py-4 border-2 border-dashed border-indigo-300 rounded-xl text-indigo-600 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 font-medium flex items-center justify-center gap-2"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
      Add Education
    </button>
  </div>
);

// Skills Section Component
const SkillsSection = ({ data, onChange, errors }) => {
  const [newSkill, setNewSkill] = React.useState('');
  const [skillCategory, setSkillCategory] = React.useState('technical');

  const predefinedSkills = {
    technical: [
      'JavaScript', 'Python', 'Java', 'React', 'Node.js', 'Angular', 'Vue.js', 'TypeScript',
      'HTML/CSS', 'SQL', 'MongoDB', 'PostgreSQL', 'AWS', 'Docker', 'Kubernetes', 'Git',
      'Machine Learning', 'Data Analysis', 'PHP', 'C++', 'C#', '.NET', 'Ruby', 'Go',
      'Swift', 'Kotlin', 'Flutter', 'React Native', 'Firebase', 'GraphQL', 'REST APIs'
    ],
    soft: [
      'Communication', 'Leadership', 'Team Work', 'Problem Solving', 'Critical Thinking',
      'Time Management', 'Project Management', 'Adaptability', 'Creativity', 'Negotiation',
      'Public Speaking', 'Mentoring', 'Strategic Planning', 'Decision Making', 'Collaboration'
    ],
    tools: [
      'Figma', 'Adobe Creative Suite', 'Sketch', 'InVision', 'Jira', 'Trello', 'Slack',
      'Microsoft Office', 'Google Workspace', 'Salesforce', 'HubSpot', 'Tableau', 'Power BI',
      'Jenkins', 'GitHub', 'GitLab', 'VS Code', 'IntelliJ', 'Postman', 'Selenium'
    ]
  };

  const addSkill = (skill) => {
    if (skill && !data.includes(skill)) {
      onChange([...data, skill]);
      setNewSkill('');
    }
  };

  const removeSkill = (skillToRemove) => {
    onChange(data.filter(skill => skill !== skillToRemove));
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Skills & Expertise <span className="text-red-500">*</span> <span className="text-gray-500">/ कौशल्ये आणि तज्ञता</span>
      </h2>
      <p className="text-gray-600 mb-6">
        Add your technical skills, soft skills, and tools you're proficient with. <span className="text-gray-500">/ तुमची तांत्रिक कौशल्ये, सॉफ्ट स्किल्स आणि साधने जोडा.</span>
      </p>

      {errors?.skills && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {errors.skills}
        </div>
      )}

      {/* Current Skills */}
      {data.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Your Skills ({data.length})</h3>
          <div className="flex flex-wrap gap-2">
            {data.map((skill, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-100 text-indigo-800 rounded-lg text-sm font-medium"
              >
                {skill}
                <button
                  onClick={() => removeSkill(skill)}
                  className="text-indigo-600 hover:text-indigo-800"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Add Custom Skill */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Add Custom Skill</h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addSkill(newSkill)}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Type a skill and press Enter"
          />
          <button
            onClick={() => addSkill(newSkill)}
            className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors"
          >
            Add
          </button>
        </div>
      </div>

      {/* Predefined Skills by Category */}
      <div className="space-y-6">
        <div>
          <div className="flex gap-2 mb-4">
            {Object.keys(predefinedSkills).map((category) => (
              <button
                key={category}
                onClick={() => setSkillCategory(category)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${skillCategory === category
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)} Skills
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {predefinedSkills[skillCategory].map((skill) => (
              <button
                key={skill}
                onClick={() => addSkill(skill)}
                disabled={data.includes(skill)}
                className={`p-3 text-left rounded-lg border transition-all ${data.includes(skill)
                  ? 'bg-green-50 border-green-200 text-green-800 cursor-not-allowed'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{skill}</span>
                  {data.includes(skill) && (
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Resume Section Component
const ResumeSection = ({ resumeURL, resumeFileName, onUpload, uploading, error }) => {
  const fileInputRef = React.useRef(null);

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileInputRef.current && !uploading) {
      fileInputRef.current.click();
    }
  };

  const handleViewResume = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (resumeURL) {
      window.open(resumeURL, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Resume <span className="text-gray-500">/ बायोडाटा</span> <span className="text-red-500">*</span>
      </h2>
      <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
        {resumeURL ? (
          <div>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-green-600 font-medium mb-2">Resume uploaded successfully!</p>
            <p className="text-gray-600 text-sm mb-4" style={{ wordBreak: 'break-all', overflowWrap: 'break-word', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{resumeFileName}</p>
            <button
              type="button"
              onClick={handleViewResume}
              className="text-indigo-600 hover:text-indigo-800 font-medium underline mb-4 inline-block touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              View Resume
            </button>
          </div>
        ) : (
          <div>
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="text-gray-600 mb-4">Upload your resume (PDF, DOC, DOCX - Max 5MB)</p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={onUpload}
          disabled={uploading}
          className="hidden"
          id="resume-upload"
          style={{ display: 'none' }}
          tabIndex={-1}
        />
        <button
          type="button"
          onClick={handleButtonClick}
          disabled={uploading}
          className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-indigo-600 hover:bg-indigo-700 transition-colors touch-manipulation ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer active:bg-indigo-800'
            }`}
          style={{ WebkitTapHighlightColor: 'transparent', minHeight: '48px', minWidth: '120px' }}
        >
          {uploading ? 'Uploading...' : resumeURL ? 'Replace Resume' : 'Upload Resume'}
        </button>

        {error && (
          <p className="text-red-600 text-sm mt-2">{error}</p>
        )}
      </div>
    </div>
  );
};

// Preferred Locations Selector Component
const PreferredLocationsSelector = ({ selectedLocations, onChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Comprehensive list of Indian Districts
  const districts = [
    "Nicobar", "North and Middle Andaman", "South Andaman", // Andaman and Nicobar Islands
    "Anantapur", "Chittoor", "East Godavari", "Guntur", "Krishna", "Kurnool", "Prakasam", "Srikakulam", "Sri Potti Sriramulu Nellore", "Visakhapatnam", "Vizianagaram", "West Godavari", "YSR District, Kadapa (Cuddapah)", // Andhra Pradesh
    "Anjaw", "Changlang", "Dibang Valley", "East Kameng", "East Siang", "Kamle", "Kra Daadi", "Kurung Kumey", "Lepa Rada", "Lohit", "Longding", "Lower Dibang Valley", "Lower Siang", "Lower Subansiri", "Namsai", "Pakke Kessang", "Papum Pare", "Shi Yomi", "Siang", "Tawang", "Tirap", "Upper Siang", "Upper Subansiri", "West Kameng", "West Siang", // Arunachal Pradesh
    "Baksa", "Barpeta", "Biswanath", "Bongaigaon", "Cachar", "Charaideo", "Chirang", "Darrang", "Dhemaji", "Dhubri", "Dibrugarh", "Dima Hasao (North Cachar Hills)", "Goalpara", "Golaghat", "Hailakandi", "Hojai", "Jorhat", "Kamrup", "Kamrup Metropolitan", "Karbi Anglong", "Karimganj", "Kokrajhar", "Lakhimpur", "Majuli", "Morigaon", "Nagaon", "Nalbari", "Sivasagar", "Sonitpur", "South Salmara-Mankachar", "Tinsukia", "Udalguri", "West Karbi Anglong", // Assam
    "Araria", "Arwal", "Aurangabad", "Banka", "Begusarai", "Bhagalpur", "Bhojpur", "Buxar", "Darbhanga", "East Champaran (Motihari)", "Gaya", "Gopalganj", "Jamui", "Jehanabad", "Kaimur (Bhabua)", "Katihar", "Khagaria", "Kishanganj", "Lakhisarai", "Madhepura", "Madhubani", "Munger (Monghyr)", "Muzaffarpur", "Nalanda", "Nawada", "Patna", "Purnia (Purnea)", "Rohtas", "Saharsa", "Samastipur", "Saran", "Sheikhpura", "Sheohar", "Sitamarhi", "Siwan", "Supaul", "Vaishali", "West Champaran", // Bihar
    "Chandigarh", // Chandigarh
    "Balod", "Baloda Bazar", "Balrampur", "Bastar", "Bemetara", "Bijapur", "Bilaspur", "Dantewada (South Bastar)", "Dhamtari", "Durg", "Gariyaband", "Janjgir-Champa", "Jashpur", "Kabirdham (Kawardha)", "Kanker (North Bastar)", "Kondagaon", "Korba", "Koriya", "Mahasamund", "Mungeli", "Narayanpur", "Raigarh", "Raipur", "Rajnandgaon", "Sukma", "Surajpur", "Surguja", // Chhattisgarh
    "Dadra and Nagar Haveli", // Dadra and Nagar Haveli
    "Daman", "Diu", // Daman and Diu
    "Central Delhi", "East Delhi", "New Delhi", "North Delhi", "North East Delhi", "North West Delhi", "Shahdara", "South Delhi", "South East Delhi", "South West Delhi", "West Delhi", // Delhi
    "North Goa", "South Goa", // Goa
    "Ahmedabad", "Amreli", "Anand", "Aravalli", "Banaskantha (Palanpur)", "Bharuch", "Bhavnagar", "Botad", "Chhota Udepur", "Dahod", "Dang (Ahwa)", "Devbhoomi Dwarka", "Gandhinagar", "Gir Somnath", "Jamnagar", "Junagadh", "Kheda (Nadiad)", "Kutch", "Mahisagar", "Mehsana", "Morbi", "Narmada (Rajpipla)", "Navsari", "Panchmahal (Godhra)", "Patan", "Porbandar", "Rajkot", "Sabarkantha (Himmatnagar)", "Surat", "Surendranagar", "Tapi (Vyara)", "Vadodara", "Valsad", // Gujarat
    "Ambala", "Bhiwani", "Charkhi Dadri", "Faridabad", "Fatehabad", "Gurugram (Gurgaon)", "Hisar", "Jhajjar", "Jind", "Kaithal", "Karnal", "Kurukshetra", "Mahendragarh", "Nuh", "Palwal", "Panchkula", "Panipat", "Rewari", "Rohtak", "Sirsa", "Sonipat", "Yamunanagar", // Haryana
    "Bilaspur", "Chamba", "Hamirpur", "Kangra", "Kinnaur", "Kullu", "Lahaul & Spiti", "Mandi", "Shimla", "Sirmaur (Sirmour)", "Solan", "Una", // Himachal Pradesh
    "Anantnag", "Bandipore", "Baramulla", "Budgam", "Doda", "Ganderbal", "Jammu", "Kathua", "Kishtwar", "Kulgam", "Kupwara", "Poonch", "Pulwama", "Rajouri", "Ramban", "Reasi", "Samba", "Shopian", "Srinagar", "Udhampur", // Jammu and Kashmir
    "Bokaro", "Chatra", "Deoghar", "Dhanbad", "Dumka", "East Singhbhum", "Garhwa", "Giridih", "Godda", "Gumla", "Hazaribagh", "Jamtara", "Khunti", "Koderma", "Latehar", "Lohardaga", "Pakur", "Palamu", "Ramgarh", "Ranchi", "Sahibganj", "Seraikela-Kharsawan", "Simdega", "West Singhbhum", // Jharkhand
    "Bagalkot", "Ballari (Bellary)", "Belagavi (Belgaum)", "Bengaluru (Bangalore) Rural", "Bengaluru (Bangalore) Urban", "Bidar", "Chamarajanagar", "Chikkaballapur", "Chikkamagaluru (Chikmagalur)", "Chitradurga", "Dakshina Kannada", "Davangere", "Dharwad", "Gadag", "Hassan", "Haveri", "Kalaburagi (Gulbarga)", "Kodagu", "Kolar", "Koppal", "Mandya", "Mysuru (Mysore)", "Raichur", "Ramanagara", "Shivamogga (Shimoga)", "Tumakuru (Tumkur)", "Udupi", "Uttara Kannada (Karwar)", "Vijayapura (Bijapur)", "Yadgir", // Karnataka
    "Alappuzha", "Ernakulam", "Idukki", "Kannur", "Kasaragod", "Kollam", "Kottayam", "Kozhikode", "Malappuram", "Palakkad", "Pathanamthitta", "Thiruvananthapuram", "Thrissur", "Wayanad", // Kerala
    "Kargil", "Leh", // Ladakh
    "Lakshadweep", // Lakshadweep
    "Agar Malwa", "Alirajpur", "Anuppur", "Ashoknagar", "Balaghat", "Barwani", "Betul", "Bhind", "Bhopal", "Burhanpur", "Chhatarpur", "Chhindwara", "Damoh", "Datia", "Dewas", "Dhar", "Dindori", "Guna", "Gwalior", "Harda", "Hoshangabad", "Indore", "Jabalpur", "Jhabua", "Katni", "Khandwa", "Khargone", "Mandla", "Mandsaur", "Morena", "Narsinghpur", "Neemuch", "Niwari", "Panna", "Raisen", "Rajgarh", "Ratlam", "Rewa", "Sagar", "Satna", "Sehore", "Seoni", "Shahdol", "Shajapur", "Sheopur", "Shivpuri", "Sidhi", "Singrauli", "Tikamgarh", "Ujjain", "Umaria", "Vidisha", // Madhya Pradesh
    "Ahilyanagar", "Akola", "Amravati", "Chhatrapati Sambhajinagar", "Beed", "Bhandara", "Buldhana", "Chandrapur", "Dhule", "Gadchiroli", "Gondia", "Hingoli", "Jalgaon", "Jalna", "Kolhapur", "Latur", "Mumbai City", "Mumbai Suburban", "Nagpur", "Nanded", "Nandurbar", "Nashik", "Dharashiv", "Palghar", "Parbhani", "Pune", "Raigad", "Ratnagiri", "Sangli", "Satara", "Sindhudurg", "Solapur", "Thane", "Wardha", "Washim", "Yavatmal", // Maharashtra
    "Bishnupur", "Chandel", "Churachandpur", "Imphal East", "Imphal West", "Jiribam", "Kakching", "Kamjong", "Kangpokpi", "Noney", "Pherzawl", "Senapati", "Tamenglong", "Tengnoupal", "Thoubal", "Ukhrul", // Manipur
    "East Garo Hills", "East Jaintia Hills", "East Khasi Hills", "North Garo Hills", "Ri Bhoi", "South Garo Hills", "South West Garo Hills", "South West Khasi Hills", "West Garo Hills", "West Jaintia Hills", "West Khasi Hills", // Meghalaya
    "Aizawl", "Champhai", "Hnahthial", "Khawzawl", "Kolasib", "Lawngtlai", "Lunglei", "Mamit", "Saiha", "Saitual", "Serchhip", // Mizoram
    "Dimapur", "Kiphire", "Kohima", "Longleng", "Mokokchung", "Mon", "Peren", "Phek", "Tuensang", "Wokha", "Zunheboto", // Nagaland
    "Angul", "Balangir", "Balasore", "Bargarh", "Bhadrak", "Boudh", "Cuttack", "Deogarh", "Dhenkanal", "Gajapati", "Ganjam", "Jagatsinghpur", "Jajpur", "Jharsuguda", "Kalahandi", "Kandhamal", "Kendrapara", "Kendujhar (Keonjhar)", "Khordha", "Koraput", "Malkangiri", "Mayurbhanj", "Nabarangpur", "Nayagarh", "Nuapada", "Puri", "Rayagada", "Sambalpur", "Subarnapur (Sonepur)", "Sundargarh", // Odisha
    "Karaikal", "Mahe", "Puducherry", "Yanam", // Puducherry
    "Amritsar", "Barnala", "Bathinda", "Faridkot", "Fatehgarh Sahib", "Fazilka", "Ferozepur", "Gurdaspur", "Hoshiarpur", "Jalandhar", "Kapurthala", "Ludhiana", "Mansa", "Moga", "Muktsar", "Nawanshahr (Shahid Bhagat Singh Nagar)", "Pathankot", "Patiala", "Rupnagar", "Sahibzada Ajit Singh Nagar (Mohali)", "Sangrur", "Tarn Taran", // Punjab
    "Ajmer", "Alwar", "Banswara", "Baran", "Barmer", "Bharatpur", "Bhilwara", "Bikaner", "Bundi", "Chittorgarh", "Churu", "Dausa", "Dholpur", "Dungarpur", "Hanumangarh", "Jaipur", "Jaisalmer", "Jalore", "Jhalawar", "Jhunjhunu", "Jodhpur", "Karauli", "Kota", "Nagaur", "Pali", "Pratapgarh", "Rajsamand", "Sawai Madhopur", "Sikar", "Sirohi", "Sri Ganganagar", "Tonk", "Udaipur", // Rajasthan
    "East Sikkim", "North Sikkim", "South Sikkim", "West Sikkim", // Sikkim
    "Ariyalur", "Chengalpattu", "Chennai", "Coimbatore", "Cuddalore", "Dharmapuri", "Dindigul", "Erode", "Kallakurichi", "Kanchipuram", "Kanyakumari", "Karur", "Krishnagiri", "Madurai", "Mayiladuthurai", "Nagapattinam", "Namakkal", "Nilgiris", "Perambalur", "Pudukkottai", "Ramanathapuram", "Ranipet", "Salem", "Sivaganga", "Tenkasi", "Thanjavur", "Theni", "Thoothukudi (Tuticorin)", "Tiruchirappalli", "Tirunelveli", "Tirupathur", "Tiruppur", "Tiruvallur", "Tiruvannamalai", "Tiruvarur", "Vellore", "Viluppuram", "Virudhunagar", // Tamil Nadu
    "Adilabad", "Bhadradri Kothagudem", "Hyderabad", "Jagtial", "Jangaon", "Jayashankar Bhoopalpally", "Jogulamba Gadwal", "Kamareddy", "Karimnagar", "Khammam", "Komaram Bheem Asifabad", "Mahabubabad", "Mahabubnagar", "Mancherial", "Medak", "Medchal", "Nagarkurnool", "Nalgonda", "Nirmal", "Nizamabad", "Peddapalli", "Rajanna Sircilla", "Rangareddy", "Sangareddy", "Siddipet", "Suryapet", "Vikarabad", "Wanaparthy", "Warangal (Rural)", "Warangal (Urban)", "Yadadri Bhuvanagiri", // Telangana
    "Dhalai", "Gomati", "Khowai", "North Tripura", "Sepahijala", "South Tripura", "Unakoti", "West Tripura", // Tripura
    "Agra", "Aligarh", "Ambedkar Nagar", "Amethi (Chatrapati Sahuji Mahraj Nagar)", "Amroha (J.P. Nagar)", "Auraiya", "Ayodhya (Faizabad)", "Azamgarh", "Baghpat", "Bahraich", "Ballia", "Balrampur", "Banda", "Barabanki", "Bareilly", "Basti", "Bhadohi", "Bijnor", "Budaun", "Bulandshahr", "Chandauli", "Chitrakoot", "Deoria", "Etah", "Etawah", "Farrukhabad", "Fatehpur", "Firozabad", "Gautam Buddha Nagar", "Ghaziabad", "Ghazipur", "Gonda", "Gorakhpur", "Hamirpur", "Hapur (Panchsheel Nagar)", "Hardoi", "Hathras", "Jalaun", "Jaunpur", "Jhansi", "Kannauj", "Kanpur Dehat", "Kanpur Nagar", "Kasganj (Kanshiram Nagar)", "Kaushambi", "Kheri", "Kushinagar (Padrauna)", "Lalitpur", "Lucknow", "Maharajganj", "Mahoba", "Mainpuri", "Mathura", "Mau", "Meerut", "Mirzapur", "Moradabad", "Muzaffarnagar", "Pilibhit", "Pratapgarh", "Prayagraj (Allahabad)", "Raebareli", "Rampur", "Saharanpur", "Sambhal (Bhim Nagar)", "Sant Kabir Nagar", "Shahjahanpur", "Shamli", "Shravasti", "Siddharthnagar", "Sitapur", "Sonbhadra", "Sultanpur", "Unnao", "Varanasi", // Uttar Pradesh
    "Almora", "Bageshwar", "Chamoli", "Champawat", "Dehradun", "Haridwar", "Nainital", "Pauri Garhwal", "Pithoragarh", "Rudraprayag", "Tehri Garhwal", "Udham Singh Nagar", "Uttarkashi", // Uttarakhand
    "Alipurduar", "Bankura", "Birbhum", "Cooch Behar", "Dakshin Dinajpur (South Dinajpur)", "Darjeeling", "Hooghly", "Howrah", "Jalpaiguri", "Jhargram", "Kalimpong", "Kolkata", "Malda", "Murshidabad", "Nadia", "North 24 Parganas", "Paschim Bardhaman (West Bardhaman)", "Paschim Medinipur (West Medinipur)", "Purba Bardhaman (East Bardhaman)", "Purba Medinipur (East Medinipur)", "Purulia", "South 24 Parganas", "Uttar Dinajpur (North Dinajpur)" // West Bengal
  ].sort();

  // Parse selected locations if it's a string
  const selectedArray = Array.isArray(selectedLocations)
    ? selectedLocations
    : typeof selectedLocations === 'string' && selectedLocations.trim() !== ''
      ? selectedLocations.split(',').map(s => s.trim())
      : [];

  const filteredDistricts = districts.filter(district =>
    district.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleLocation = (district) => {
    let newSelection;
    if (selectedArray.includes(district)) {
      newSelection = selectedArray.filter(d => d !== district);
    } else {
      newSelection = [...selectedArray, district];
    }
    // Return as string to match previous behavior/backend expectation
    onChange(newSelection.join(', '));
  };

  const removeLocation = (district) => {
    const newSelection = selectedArray.filter(d => d !== district);
    onChange(newSelection.join(', '));
  };

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Work Locations</label>

      {/* Search Input */}
      <div className="relative mb-3">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="Search for districts (e.g., Pune, Mumbai, Bangalore)"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Dropdown List */}
      {isOpen && searchTerm && (
        <div className="absolute z-10 w-full max-w-md bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto mt-1">
          {filteredDistricts.length > 0 ? (
            filteredDistricts.map(district => (
              <div
                key={district}
                onClick={() => {
                  toggleLocation(district);
                  setSearchTerm('');
                  setIsOpen(false);
                }}
                className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 flex items-center justify-between ${selectedArray.includes(district) ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                  }`}
              >
                <span>{district}</span>
                {selectedArray.includes(district) && (
                  <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            ))
          ) : (
            <div className="px-4 py-3 text-gray-500 text-center">No districts found</div>
          )}
        </div>
      )}

      {/* Selected Tags */}
      <div className="flex flex-wrap gap-2 mt-2">
        {selectedArray.map((location, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm"
          >
            {location}
            <button
              onClick={() => removeLocation(location)}
              className="text-indigo-600 hover:text-indigo-800 focus:outline-none"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-2">Select multiple locations where you are willing to work.</p>
    </div>
  );
};

// Job Preferences Section Component
const JobPreferencesSection = ({ data, onChange, errors = {} }) => {
  const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship', 'Temporary'];
  const workModes = ['Work From Home', 'In Field', 'Work From Office', 'Hybrid'];
  const currencies = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD'];
  const noticePeriods = ['Immediate', '1 week', '2 weeks', '1 month', '2 months', '3 months'];

  const toggleArrayItem = (array, item) => {
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    } else {
      return [...array, item];
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Job Preferences <span className="text-gray-500">/ नोकरी प्राधान्ये</span>
      </h2>
      <p className="text-gray-600 mb-6">
        Help employers understand what you're looking for in your next role. <span className="text-gray-500">/ नियोक्त्यांना तुम्ही तुमच्या पुढील भूमिकेत काय शोधत आहात ते समजण्यास मदत करा.</span>
      </p>

      <div className="space-y-8">
        {/* Job Roles */}
        <div>
          <BilingualLabel english="Preferred Job Roles" marathi="पसंतीच्या नोकरी भूमिका" required className="mb-3" />
          <p className="text-xs text-gray-500 mb-2">Add job roles you are interested in (e.g. Web Developer, Sales Manager)</p>
          <EnhancedChipInput
            label="Preferred Job Roles"
            value={data.targetRoles || []}
            onChange={(newRoles) => onChange('targetRoles', newRoles)}
            suggestions={commonJobRoles.filter(role => role !== 'All Roles')}
            placeholder="Type a role or select from suggestions"
            maxVisibleSuggestions={10}
            allowCustom={true}
          />
          {errors['preferences.targetRoles'] && (
            <p className="text-red-600 text-sm mt-1">{errors['preferences.targetRoles']}</p>
          )}
        </div>

        {/* Job Types */}
        <div>
          <BilingualLabel english="Preferred Job Types" marathi="पसंतीचे नोकरी प्रकार" required className="mb-3" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {jobTypes.map((type) => (
              <label key={type} className="flex items-center">
                <input
                  type="checkbox"
                  checked={data.jobTypes.includes(type)}
                  onChange={() => onChange('jobTypes', toggleArrayItem(data.jobTypes, type))}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm text-gray-700">{type}</span>
              </label>
            ))}
          </div>
          {errors['preferences.jobTypes'] && (
            <p className="text-red-600 text-sm mt-1">{errors['preferences.jobTypes']}</p>
          )}
        </div>

        {/* Work Modes */}
        <div>
          <BilingualLabel english="Preferred Work Modes" marathi="पसंतीचे कामाचे प्रकार" required className="mb-3" />
          <div className="grid grid-cols-2 gap-3">
            {workModes.map((mode) => (
              <label key={mode} className="flex items-center">
                <input
                  type="checkbox"
                  checked={data.workModes.includes(mode)}
                  onChange={() => onChange('workModes', toggleArrayItem(data.workModes, mode))}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm text-gray-700">{mode}</span>
              </label>
            ))}
          </div>
          {errors['preferences.workModes'] && (
            <p className="text-red-600 text-sm mt-1">{errors['preferences.workModes']}</p>
          )}
        </div>

        {/* Expected Salary */}
        <div>
          <BilingualLabel english="Expected Salary Range (Annual)" marathi="अपेक्षित पगार श्रेणी (वार्षिक)" required className="mb-3" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Currency</label>
              <select
                value={data.expectedSalary.currency}
                onChange={(e) => onChange('expectedSalary', { ...data.expectedSalary, currency: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {currencies.map((currency) => (
                  <option key={currency} value={currency}>{currency}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Minimum</label>
              <input
                type="number"
                value={data.expectedSalary.min}
                onChange={(e) => onChange('expectedSalary', { ...data.expectedSalary, min: e.target.value })}
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors['preferences.expectedSalary.min'] ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="50000"
              />
              {errors['preferences.expectedSalary.min'] && (
                <p className="text-red-600 text-sm mt-1">{errors['preferences.expectedSalary.min']}</p>
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Maximum</label>
              <input
                type="number"
                value={data.expectedSalary.max}
                onChange={(e) => onChange('expectedSalary', { ...data.expectedSalary, max: e.target.value })}
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors['preferences.expectedSalary.max'] ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="80000"
              />
              {errors['preferences.expectedSalary.max'] && (
                <p className="text-red-600 text-sm mt-1">{errors['preferences.expectedSalary.max']}</p>
              )}
            </div>
          </div>
        </div>

        {/* Notice Period */}
        <div>
          <BilingualLabel english="Notice Period" marathi="नोटीस कालावधी" required className="mb-3" />
          <select
            value={data.noticePeriod}
            onChange={(e) => onChange('noticePeriod', e.target.value)}
            className={`w-full md:w-1/2 px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors['preferences.noticePeriod'] ? 'border-red-300' : 'border-gray-300'}`}
          >
            <option value="">Select notice period</option>
            {noticePeriods.map((period) => (
              <option key={period} value={period}>{period}</option>
            ))}
          </select>
          {errors['preferences.noticePeriod'] && (
            <p className="text-red-600 text-sm mt-1">{errors['preferences.noticePeriod']}</p>
          )}
        </div>

        {/* Willing to Relocate */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={data.willingToRelocate}
              onChange={(e) => onChange('willingToRelocate', e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="ml-2 text-sm font-medium text-gray-700">I am willing to relocate for the right opportunity</span>
          </label>
        </div>

        {/* Preferred Locations */}
        <PreferredLocationsSelector
          selectedLocations={data.preferredLocations || []}
          onChange={(locations) => onChange('preferredLocations', locations)}
        />

        {/* Career Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Career Level</label>
          <select
            value={data.careerLevel || ''}
            onChange={(e) => onChange('careerLevel', e.target.value)}
            className="w-full md:w-1/2 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Select career level</option>
            <option value="fresher">Fresher</option>
            <option value="mid">Mid Level</option>
            <option value="advance">Advance Level</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default SeekerProfile;
